import join from 'url-join';
import { asyncAction, createAction, http } from '../../util';
import { aggregateDataByEntity, generateSortQuery, handleError } from '../util';
import { getSelectedAccount, getSelectedAccountId } from './selectors';
import { chunk } from '../../util/lodash';
import { showToast } from '../../components/toast/actions';

const api2 = join(import.meta.env.VITE_API2_URL, 'v1');

export const selectAdminAccount = createAction('SELECT_ADMIN_ACCOUNT');
export const clearAdminAccount = createAction('CLEAR_ADMIN_ACCOUNT');
export const clearAdminAccountCatalogs = createAction('CLEAR_ADMIN_ACCOUNT_CATALOGS');
export const clearAdminAccountContractDetails = createAction('CLEAR_ADMIN_ACCOUNT_CONTRACT_DETAILS');

export const setAdminDepartmentCatalogTypes = createAction('SET_ADMIN_DEPARTMENT_CATALOG_TYPES');
export const bulkSetAdminDepartmentCatalogTypes = createAction('BULK_SET_ADMIN_DEPARTMENT_CATALOG_TYPES');

export const fetchAdminAccounts = asyncAction({
  baseName: 'FETCH_ADMIN_ACCOUNTS',
  action: action => () => dispatch => {
    dispatch(action.start());

    return http
      .get(join(api2, '/accounts?count=10000&sort=nameAsc'))
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => handleError(dispatch, action, error));
  }
});

export const updateAdminDepartmentCatalogTypes = asyncAction({
  baseName: 'UPDATE_ADMIN_DEPARTMENT_CATALOG_TYPES',
  action:
    action =>
    ({ departmentId, catalogTypeIds }) =>
    dispatch => {
      dispatch(action.start());

      const payload = { catalogTypes: catalogTypeIds.map(id => ({ id })) };

      return http
        .post(join(api2, `/departments/${departmentId}/catalogTypes`), payload)
        .then(() => action.success())
        .catch(error => handleError(dispatch, action, error));
    }
});

const batchUpdateDepartmentCatalogTypes = async departmentIdToCatalogTypes => {
  const departmentIds = Object.keys(departmentIdToCatalogTypes);
  const chunkedDepartments = chunk(departmentIds, 20);

  await Promise.all(
    chunkedDepartments.map(chunk => {
      const payload = {
        requests: chunk.map(deptId => ({
          id: deptId,
          url: `/v1/departments/${deptId}/catalogTypes`,
          method: 'POST',
          body: { catalogTypes: departmentIdToCatalogTypes[deptId].map(ct => ({ id: ct.id })) }
        }))
      };

      return http.post(`${api2}/$batch`, payload).catch(error => console.error(error));
    })
  );
};

export const bulkUpdateAdminDepartmentCatalogTypes = asyncAction({
  baseName: 'BULK_UPDATE_ADMIN_DEPARTMENT_CATALOG_TYPES',
  action:
    action =>
    ({ departmentIdToCatalogTypes }) =>
    dispatch => {
      dispatch(action.start());

      return batchUpdateDepartmentCatalogTypes(departmentIdToCatalogTypes)
        .then(() => {
          action.success();
          dispatch(showToast({ message: 'Catalog Type Added to Department(s)', type: 'success' }));
        })
        .catch(error => handleError(dispatch, action, error));
    }
});

export const saveAdminDepartmentCatalogTypes =
  ({ departmentId, catalogTypeIds }) =>
  async (dispatch, getState) => {
    const state = getState();
    const accountId = getSelectedAccount(state).id;

    const event = await dispatch(updateAdminDepartmentCatalogTypes({ departmentId, catalogTypeIds }));

    if (event && event.type === updateAdminDepartmentCatalogTypes.event.success) {
      return dispatch(setAdminDepartmentCatalogTypes({ accountId, departmentId, catalogTypeIds }));
    }

    return Promise.resolve();
  };

export const bulkSaveAdminDepartmentCatalogTypes =
  ({ departmentIdToCatalogTypes }) =>
  async (dispatch, getState) => {
    const state = getState();
    const accountId = getSelectedAccount(state).id;

    const event = await dispatch(bulkUpdateAdminDepartmentCatalogTypes({ departmentIdToCatalogTypes }));

    if (event && event.type === bulkUpdateAdminDepartmentCatalogTypes.event.success) {
      return dispatch(bulkSetAdminDepartmentCatalogTypes({ accountId, departmentIdToCatalogTypes }));
    }

    return Promise.resolve();
  };

export const fetchAdminAccountDepartments = asyncAction({
  baseName: 'FETCH_ADMIN_ACCOUNT_DEPARTMENTS',
  action: action => adminAccountId => (dispatch, getState) => {
    dispatch(action.start());

    const state = getState();
    const accountId = adminAccountId || getSelectedAccountId(state);

    return http
      .get(
        join(
          api2,
          `/accounts/${accountId}/departments?${generateSortQuery(
            state.tables.adminAccountDepartments.sortBy
          )}&count=500`
        )
      )
      .then(async ({ data: response }) => {
        const { objects: departments } = response;
        const chunkedDepartments = chunk(departments, 20);

        // Fetch catalogTypes for all departments using Batch API
        await Promise.all(
          chunkedDepartments.map(chunk => {
            const payload = {
              requests: chunk.map(({ id }) => ({
                id,
                url: `/v1/departments/${id}/catalogTypes`,
                method: 'GET'
              }))
            };

            return http
              .post(`${api2}/$batch`, payload)
              .then(({ data: { responses } }) => {
                // Merge catalogType response data with department list fetched above
                responses.forEach(({ id, body: { objects: catalogTypes }, status }) => {
                  const department = departments.find(department => department.id === id);
                  department.catalogTypes = status === 200 ? catalogTypes.map(({ id }) => id) : [];
                });
              })
              .catch(error => console.error(error));
          })
        );

        dispatch(action.success({ adminAccountId: accountId, departments }));

        return response.departments;
      })
      .catch(() => handleError(dispatch, action, { adminAccountId: accountId }));
  }
});

export const fetchAdminAccountCatalogs = ({ filters, sortBy } = {}) =>
  aggregateDataByEntity({
    baseName: 'FETCH_ADMIN_ACCOUNT_CATALOGS',
    entity: 'catalogs',
    fields: 'id,name,catalogTypes,seasons,atOnceStart,atOnceEnd,futuresStart,futuresEnd,forecastStart,forecastEnd',
    constant: 30,
    callback: array =>
      // Adaptation
      array.map(catalog => ({
        ...catalog,
        catalogTypes: catalog.catalogTypes.map(type => type.id)
      })),
    forSelectedAccount: true,
    filters,
    sortBy
  });

export const fetchAdminAccountContracts = () =>
  aggregateDataByEntity({
    baseName: 'FETCH_ADMIN_ACCOUNT_CONTRACTS',
    entity: 'contracts',
    constant: 30,
    callback: array =>
      array.map(({ contractGroups, ...rest }) =>
        // Adaptation
        contractGroups.length > 1 // If more than one group is present, create contract objects accordingly
          ? contractGroups.map(group => ({
              ...rest,
              groupName: group.name
            }))
          : {
              ...rest,
              groupName: contractGroups?.[0]?.name || '' // Auto-assigned contract will not reflect a group
            }
      ),
    forSelectedAccount: true
  });

export const fetchSelectedAccountDepartments = asyncAction({
  baseName: 'FETCH_SELECTED_ACCOUNT_DEPARTMENTS',
  action: action => accountId => dispatch => {
    dispatch(action.start());

    return http
      .get(join(api2, `/departments?filter=accountId(${accountId})&count=100`))
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => handleError(dispatch, action, error));
  }
});

export const addContractDepartmentAssociation = asyncAction({
  baseName: 'ADD_CONTRACT_DEPARTMENT_ASSOCIATION',
  action:
    action =>
    ({ contractId, departmentNumber }) =>
    dispatch => {
      dispatch(action.start());

      return http
        .post(join(api2, `/contract/${contractId}/department`), { departmentNumber })
        .then(() => {
          dispatch(action.success());
          dispatch(showToast({ message: 'Department associated', type: 'success' }));
        })
        .catch(error => handleError(dispatch, action, error));
    }
});

export const removeContractDepartmentAssociation = asyncAction({
  baseName: 'REMOVE_CONTRACT_DEPARTMENT_ASSOCIATION',
  action:
    action =>
    ({ contractId, departmentNumber }) =>
    dispatch => {
      dispatch(action.start());

      return http
        .delete(
          join(api2, `/contract/${contractId}/department`),
          { departmentNumber },
          { 'Content-Type': 'application/json' }
        )
        .then(() => {
          dispatch(action.success());
          dispatch(showToast({ message: 'Department unassociated', type: 'success' }));
        })
        .catch(error => handleError(dispatch, action, error));
    }
});

export const updateDepartmentPoPrefix = asyncAction({
  baseName: 'UPDATE_DEPARTMENT_PO_PREFIX',
  action:
    action =>
    ({ departmentId, poPrefix }) =>
    dispatch => {
      dispatch(action.start());

      return http
        .patch(join(api2, `/departments/${departmentId}`), { poPrefix })
        .then(() => {
          dispatch(action.success());
          dispatch(showToast({ message: 'PO Prefix updated', type: 'success' }));
        })
        .catch(error => handleError(dispatch, action, error));
    }
});
